import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  fetchSavedFiltersByView,
  fetchSubmissions,
  fetchSubmissionsDashBoardConfig,
  getMySubmissionFilter,
  IMySubmissionFilter,
  ISubmissionSortOption
} from 'src/api/queries';

const LIMIT = 10;

export const useSubmissions = <T>(
  submissionType: 'draft' | 'active',
  page: number,
  offset: number,
  filters?: IMySubmissionFilter,
  sort?: ISubmissionSortOption,
  limit: number = LIMIT
) => {
  return useInfiniteQuery({
    retry: 0,
    queryKey: [
      `my-submissions-${submissionType}`,
      { filters, sort, page, limit }
    ],
    queryFn: () =>
      fetchSubmissions<T>({
        submissionType,
        limit,
        offset: offset,
        filters,
        sort
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage) return undefined;
      const maxOffset = lastPage.total;
      const nextOffset = allPages.length * limit;
      return nextOffset < maxOffset ? nextOffset : undefined;
    },
    select: data => ({
      ...data,
      items: data.pages.flatMap(page => page.items),
      total: data.pages[0]?.total ?? 0
    })
  });
};

export const useSubmissionFilters = () =>
  useQuery({
    queryKey: ['my-submissions-filter'],
    queryFn: getMySubmissionFilter
  });

export const useSubmissionDashBoardConfig = (
  view: string,
  workspaceId?: string
) =>
  useQuery({
    queryKey: ['submission-dashboard-config', { view, workspaceId }],
    queryFn: () => fetchSubmissionsDashBoardConfig({ view, workspaceId })
  });

export const defaultValues: IMySubmissionFilter = {
  workflowPhase: [],
  journalId: [],
  searchParams: ''
};

export const useSavedFilters = (view: string) => {
  return useQuery({
    queryKey: ['submission-filter', { view }],
    queryFn: () => (view ? fetchSavedFiltersByView(view) : null)
  });
};
